import React from 'react'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { AnimateHeight, ChevronDownExactIcon } from '@root/shared/ui-kit'

import type { IconProps } from '@root/shared/ui-kit'

type ExpandableSelectCardProps = {
  selected: boolean
  onSelect: () => void
  icon: React.FC<IconProps>
  title: React.ReactNode
  description: React.ReactNode
  toolTipMessage?: string
  children?: React.ReactNode
}

export const ExpandableSelectCard: React.FC<ExpandableSelectCardProps> = ({
  selected,
  onSelect,
  icon: Icon,
  title,
  description,
  toolTipMessage,
  children,
}) => {
  const hasContent = React.Children.toArray(children).some(Boolean)

  return (
    <Card data-selected={selected || undefined}>
      <CardHeader type="button" onClick={onSelect}>
        <Radio data-selected={selected || undefined} />
        <Separator />
        <CardIcon data-selected={selected || undefined}>
          <Icon size={22} />
        </CardIcon>
        <CardInfo>
          <TooltipStyled content={toolTipMessage} side="top" align="start">
            <CardTitle>{title}</CardTitle>
          </TooltipStyled>
          <CardDescription>{description}</CardDescription>
        </CardInfo>
        {hasContent && (
          <Chevron data-open={selected || undefined}>
            <ChevronDownExactIcon size={12} />
          </Chevron>
        )}
      </CardHeader>
      {hasContent && (
        <AnimateHeight>
          {selected && <CardContent>{children}</CardContent>}
        </AnimateHeight>
      )}
    </Card>
  )
}

const Card = styled.div`
  border: 1px solid var(--backgroundA-3);
  background: transparent;

  border-radius: 12px;
  overflow: hidden;
  transition:
    border-color 0.15s linear,
    background 0.15s linear;

  &[data-selected] {
    border-color: var(--up-8);
    background: var(--upA-2);
  }
`

const CardHeader = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  font-family: inherit;

  display: grid;
  grid-template-columns: auto auto auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;

  padding: 10px 12px;

  text-align: start;
  cursor: pointer;
`

const Radio = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--background-7);

  display: grid;
  place-items: center;

  transition: border-color 0.15s linear;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: transparent;
    transition: background 0.15s linear;
  }

  &[data-selected] {
    border-color: var(--up-9);

    &::after {
      background: var(--up-9);
    }
  }
`

const CardIcon = styled.div`
  color: var(--text-12);

  display: grid;
  place-items: center;

  transition: color 0.15s linear;
`

const CardInfo = styled.div`
  display: grid;
  gap: 2px;
`

const TooltipStyled = styled(Tooltip)`
  max-width: 300px;
`

const CardTitle = styled.div`
  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;

  width: fit-content;
`

const CardDescription = styled.div`
  color: var(--text-9);
  font-size: 11px;
`

const Chevron = styled.div`
  color: var(--text-9);

  display: grid;
  place-items: center;

  transform: rotate(0deg);
  transition: transform 0.15s linear;

  &[data-open] {
    transform: rotate(180deg);
  }
`

const CardContent = styled.div`
  padding: 0 12px 12px;
`

const Separator = styled.div`
  width: 1px;
  height: 30px;
  border-radius: 2px;
  background: var(--backgroundA-4);
`
